import Inputmask from "inputmask"

/**
 * Sistema de Máscaras de Input Simplificado
 * 
 * Este arquivo aplica máscaras automaticamente baseadas em:
 * 1. Nome dos campos (name*="phone", name*="cep", etc.)
 * 2. Classes CSS (.phone-mask, .date-picker, etc.)
 * 
 * Máscaras disponíveis:
 * - Telefone: (99) 9999-9999 ou (99) 99999-9999
 * - CEP: 99999-999
 * - CPF: 999.999.999-99
 * - CNPJ: 99.999.999/9999-99
 * - Data: 99/99/9999 (convertido para YYYY-MM-DD no envio)
 * - Data/Hora: 99/99/9999 99:99 (convertido para YYYY-MM-DDTHH:MM no envio)
 * - Hora: 99:99 (mantido como HH:MM)
 * - Moeda: R$ 9.999,99 (convertido para 9999.99 no envio)
 * 
 * IMPORTANTE:
 * - As máscaras são automaticamente removidas no envio do formulário!
 * - Datas são exibidas em formato brasileiro (DD/MM/YYYY) mas salvas em formato internacional (YYYY-MM-DD)
 * - Para campos monetários, use campos do tipo :string com a classe .currency-mask
 * 
 * Para usar em qualquer formulário:
 * - Telefones: adicione "phone" no name do campo ou classe .phone-mask
 * - CEP: adicione "cep" ou "zip_code" no name do campo ou classe .cep-mask
 * - CPF: adicione "cpf" no name do campo ou classe .cpf-mask
 * - CNPJ: adicione "cnpj" no name do campo ou classe .cnpj-mask
 * - Data: adicione classe .date-picker ou "date" no name (exceto datetime)
 * - Hora: adicione classe .time-picker ou "time" no name (exceto datetime)
 * - Moeda: adicione classe .currency-mask
 */

function applyInputMasks() {    
    try {
      // Configurações de máscaras por tipo de campo
      const maskConfigs = {
        phone: {
          mask: ["(99) 9999-9999", "(99) 99999-9999"],
          placeholder: "(00) 00000-0000",
          autoUnmask: true,
          removeMaskOnSubmit: true,
          showMaskOnHover: false
        },
        cep: {
          mask: "99999-999",
          placeholder: "00000-000",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        cpf: {
          mask: "999.999.999-99",
          placeholder: "000.000.000-00",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        cnpj: {
          mask: "99.999.999/9999-99",
          placeholder: "00.000.000/0000-00",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        date: {
          mask: "99/99/9999",
          placeholder: "dd/mm/aaaa",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        datetime: {
          mask: "99/99/9999 99:99",
          placeholder: "dd/mm/aaaa hh:mm",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        time: {
          mask: "99:99",
          placeholder: "hh:mm",
          autoUnmask: true,
          removeMaskOnSubmit: true
        },
        currency: {
          alias: 'numeric',
          prefix: 'R$ ',
          groupSeparator: '.',
          radixPoint: ',',
          digits: 2,
          digitsOptional: false,
          autoUnmask: true,
          removeMaskOnSubmit: true,
          rightAlign: false,
          placeholder: '0',
          allowMinus: false,
          min: 0,
          // Configuração para garantir formato correto
          onBeforeMask: function(value) {
            // Se o valor vier do banco, converter para formato brasileiro
            if (value && !isNaN(parseFloat(value))) {
              return parseFloat(value).toFixed(2).replace('.', ',');
            }
            return value;
          }
        }
      };

      // Aplicar máscaras baseadas em seletores
      const fieldMappings = [
        { 
          selectors: ['input[name*="phone"]', 'input.phone-mask'], 
          config: maskConfigs.phone, 
          name: 'telefone' 
        },
        { 
          selectors: ['input[name*="zip_code"]', 'input[name*="cep"]', 'input.cep-mask'], 
          config: maskConfigs.cep, 
          name: 'CEP' 
        },
        { 
          selectors: ['input[name*="cpf"]', 'input.cpf-mask'], 
          config: maskConfigs.cpf, 
          name: 'CPF' 
        },
        { 
          selectors: ['input[name*="cnpj"]', 'input.cnpj-mask'], 
          config: maskConfigs.cnpj, 
          name: 'CNPJ' 
        },
        { 
          selectors: [
            'input.date-picker', 
            'input[name*="date"]:not([name*="datetime"])', 
            'input[data-datePicker]'
          ], 
          config: maskConfigs.date, 
          name: 'data' 
        },
        { 
          selectors: [
            'input.datetime-picker', 
            'input[name*="datetime"]', 
            'input[data-dateTimePicker]'
          ], 
          config: maskConfigs.datetime, 
          name: 'data/hora' 
        },
        { 
          selectors: [
            'input.time-picker', 
            'input[name*="time"]:not([name*="datetime"])', 
            'input[data-timePicker]'
          ], 
          config: maskConfigs.time, 
          name: 'hora' 
        },
        { 
          selectors: ['input.currency-mask'], 
          config: maskConfigs.currency, 
          name: 'moeda' 
        }
      ];

      fieldMappings.forEach(({ selectors, config, name }) => {
        const inputs = document.querySelectorAll(selectors.join(', '));
        if (inputs.length > 0) {
          inputs.forEach(input => {
            try {
              // Pula campos que já têm flatpickr (eles têm seu próprio sistema de formatação)
              if (input.closest('.flatpickr-input') || input._flatpickr) {
                return;
              }
              
              // Remove máscara existente se houver
              if (input.inputmask) {
                input.inputmask.remove();
              }
              
              // Aplica a nova máscara
              Inputmask(config).mask(input);
              
              // Para campos monetários, deixar o onBeforeMask lidar com a formatação
              // O valor inicial será processado automaticamente pela máscara
              
              // Marca o campo como tendo máscara para remoção posterior
              input.setAttribute('data-has-mask', 'true');
              input.setAttribute('data-mask-type', name);
              
            } catch (e) {
              console.warn(`Erro ao aplicar máscara de ${name}:`, e);
            }
          });
        }
      });

      // Configurar handlers para remoção de máscaras no submit
      setupFormSubmitHandlers();

    } catch (error) {
      console.error('Erro geral ao aplicar máscaras:', error);
    }
}

function setupFormSubmitHandlers() {
  // Remove handlers existentes para evitar duplicação
  document.removeEventListener('submit', handleFormSubmit, true);
  
  // Adiciona handler para todos os formulários
  document.addEventListener('submit', handleFormSubmit, true);
}

function handleFormSubmit(event) {
  const form = event.target;
  
  if (form.tagName === 'FORM') {
    
    // Remove máscaras de todos os inputs com máscara no formulário
    const maskedInputs = form.querySelectorAll('input[data-has-mask="true"]');
    
    maskedInputs.forEach(input => {
      try {
        const maskType = input.getAttribute('data-mask-type');
        let processedValue = '';
        
        if (input.inputmask) {
          // Usa o método nativo do inputmask
          processedValue = input.inputmask.unmaskedvalue();
        } else {
          // Fallback: remove caracteres comuns de máscara
          processedValue = removeMaskCharacters(input.value, maskType);
        }
        
        // Conversão especial para datas: converte DD/MM/YYYY para YYYY-MM-DD
        if (maskType === 'data' || maskType === 'data/hora' || maskType === 'hora') {
          processedValue = convertDateToInternationalFormat(input.value, maskType);
        }
        
        // Atualiza o valor do campo
        input.value = processedValue;
        
      } catch (error) {
        console.warn(`Erro ao remover máscara do campo ${input.name || input.id}:`, error);
        
        // Fallback final: processar valores básicos
        const maskType = input.getAttribute('data-mask-type');
        if (maskType === 'data' || maskType === 'data/hora' || maskType === 'hora') {
          input.value = convertDateToInternationalFormat(input.value, maskType);
        } else {
          input.value = removeMaskCharacters(input.value, maskType);
        }
      }
    });
    
  }
}

// Função para converter datas do formato brasileiro (DD/MM/YYYY) para internacional (YYYY-MM-DD)
function convertDateToInternationalFormat(value, maskType) {
  if (!value || !value.trim()) return '';
  
  try {
    if (maskType === 'data') {
      // Formato: DD/MM/YYYY → YYYY-MM-DD
      const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        return `${year}-${month}-${day}`;
      }
    } else if (maskType === 'data/hora') {
      // Formato: DD/MM/YYYY HH:MM → YYYY-MM-DDTHH:MM
      const datetimeMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
      if (datetimeMatch) {
        const [, day, month, year, hour, minute] = datetimeMatch;
        return `${year}-${month}-${day}T${hour}:${minute}`;
      }
    } else if (maskType === 'hora') {
      // Formato: HH:MM → mantém HH:MM (já no formato correto)
      const timeMatch = value.match(/^(\d{2}):(\d{2})$/);
      if (timeMatch) {
        return value; // Hora já está no formato correto
      }
    }
    
    // Se não conseguir converter, retorna valor original limpo
    console.warn(`Não foi possível converter a data: ${value}`);
    return value.replace(/[\/\s\:]/g, '');
    
  } catch (error) {
    console.error(`Erro ao converter data ${value}:`, error);
    return value.replace(/[\/\s\:]/g, '');
  }
}

// Função fallback para remover caracteres de máscara manualmente
function removeMaskCharacters(value, maskType) {
  if (!value) return '';
  
  switch (maskType) {
    case 'telefone':
      return value.replace(/[\(\)\-\s]/g, ''); // Remove (, ), -, espaços
    case 'CEP':
      return value.replace(/[\-]/g, ''); // Remove -
    case 'CPF':
      return value.replace(/[\.\-]/g, ''); // Remove . e -
    case 'CNPJ':
      return value.replace(/[\.\-\/]/g, ''); // Remove ., -, /
    case 'data':
    case 'data/hora':
    case 'hora':
      // Para datas e horas, usar a função de conversão específica
      return convertDateToInternationalFormat(value, maskType);
    case 'moeda':
      // Limpar valor monetário: R$ 1.234,56 -> 1234.56
      let cleanValue = value.toString();
      // Remove R$ e espaços
      cleanValue = cleanValue.replace(/^R\$\s*/, '');
      // Remove pontos separadores de milhares (mas não o decimal)
      cleanValue = cleanValue.replace(/\.(?=\d{3})/g, '');
      // Converte vírgula decimal para ponto
      cleanValue = cleanValue.replace(',', '.');
      // Remove qualquer caractere não numérico exceto ponto
      cleanValue = cleanValue.replace(/[^\d\.]/g, '');
      return cleanValue;
    default:
      return value.replace(/[\(\)\-\.\s\/\:]/g, ''); // Remove caracteres comuns
  }
}

// Aplicar máscaras quando a página carrega
document.addEventListener('turbo:load', applyInputMasks);
document.addEventListener('turbo:render', applyInputMasks);
document.addEventListener('DOMContentLoaded', applyInputMasks);